// iter 153 - Post-payment upsell screen.
// URL: /quiz-upsell?session_id={CHECKOUT_SESSION_ID}
//
// Shown ONCE immediately after the quiz-lead Stripe payment succeeds.
// Offers two upgrades on top of Blueprint Only ($199):
//   - Essentials  → +$150 (total $349)
//   - Guided      → +$350 (total $549)
// Picking an upgrade creates a fresh Stripe Checkout Session for just
// the delta amount. Skipping continues to /quiz-claim. The page hides
// the Header/Footer nav by design - no escape hatches mid-funnel.

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { PRICING_CONTENT } from '../constants/pricingContent';

const API = process.env.REACT_APP_BACKEND_URL || '';

// iter 176 - upsell tiers derive their copy from the single-source
// constants/pricingContent.js file. The delta-price (addDollars) still
// lives here because it is upsell-flow-specific math on top of the
// already-paid Blueprint Only price.
const BLUEPRINT_ONLY_PRICE = PRICING_CONTENT.blueprint_only.price;

const UPSELLS = [
  {
    id: 'essentials',
    badge: null,
  },
  {
    id: 'guided',
    badge: 'MOST POPULAR',
  },
].map((u) => {
  const pc = PRICING_CONTENT[u.id];
  return {
    ...u,
    title: pc.name,
    tagline: pc.tagline,
    description: pc.description,
    total: pc.price,
    addDollars: pc.price - BLUEPRINT_ONLY_PRICE,
    benefits: pc.bullets,
  };
});

const QuizUpsellPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id') || '';
  const [validating, setValidating] = useState(true);
  const [validSession, setValidSession] = useState(false);
  const [busy, setBusy] = useState(null); // 'essentials' | 'guided' | 'skip'
  const [error, setError] = useState('');

  // Verify the session before showing the upsell. A bogus or unpaid
  // session_id should bounce back to the calculator rather than render
  // the upsell card UI.
  useEffect(() => {
    if (!sessionId) {
      setValidating(false);
      setValidSession(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/tools/checkout/quiz-session/${encodeURIComponent(sessionId)}`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        setValidSession(!!(r.ok && d.paid && d.flow === 'quiz_lead_direct'));
      } catch {
        if (!cancelled) setValidSession(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  const handleUpgrade = async (targetTier) => {
    if (busy) return;
    setBusy(targetTier);
    setError('');
    try {
      const r = await fetch(`${API}/api/tools/checkout/quiz-upgrade`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, target_tier: targetTier }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.checkout_url) {
        window.location.href = d.checkout_url;
        return;
      }
      setError(d.detail || 'Could not start the upgrade. Try the No thanks link below.');
      setBusy(null);
    } catch {
      setError('Network issue starting the upgrade. Try the No thanks link below.');
      setBusy(null);
    }
  };

  const handleSkip = () => {
    if (busy) return;
    setBusy('skip');
    navigate(`/quiz-claim?session_id=${encodeURIComponent(sessionId)}`, { replace: true });
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <Loader2 className="w-7 h-7 text-[#1A5C6E] animate-spin" />
      </div>
    );
  }
  if (!validSession) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
          <h1 className="font-display italic text-2xl text-[#0A1F2E] mb-3">
            We couldn&rsquo;t verify your payment.
          </h1>
          <p className="text-sm text-[#0A1F2E]/70 mb-5">
            Your session link looks expired or invalid. If you just completed checkout, please reload - Stripe may still be confirming.
          </p>
          <a
            href="/mexico-cost-of-living-calculator"
            className="inline-block bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-semibold px-5 py-2.5 rounded-full text-sm"
          >
            Back to calculator
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6EF] py-12 md:py-20 px-6" data-testid="quiz-upsell-page">
      <div className="max-w-4xl mx-auto">
        {/* Small confirmation chip - gives reassurance before the upsell ask */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <CheckCircle2 className="w-5 h-5 text-[#2CA8A0]" />
          <p className="font-mono uppercase text-[10px] tracking-[0.22em] text-[#1A5C6E]">
            Payment confirmed
          </p>
        </div>
        <header className="text-center mb-10">
          <h1
            className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3"
            data-testid="quiz-upsell-h1"
          >
            You&rsquo;re in. Want to get more out of your move?
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/70 max-w-2xl mx-auto">
            One-time decision. You&rsquo;ve already paid for Blueprint Only. Add to it today only.
          </p>
        </header>

        {/* Two cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-7">
          {UPSELLS.map((u) => (
            <article
              key={u.id}
              data-testid={`quiz-upsell-card-${u.id}`}
              className="relative bg-white border-2 border-[#EFE6CF] hover:border-[#2CA8A0] rounded-2xl p-7 flex flex-col transition-colors"
            >
              {u.badge && (
                <span className="absolute -top-2 right-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.16em]">
                  <Sparkles className="w-3 h-3" /> {u.badge}
                </span>
              )}
              <h2 className="font-display italic text-3xl text-[#0A1F2E] mb-1">{u.title}</h2>
              {u.tagline && (
                <p className="font-body italic text-sm text-[#1A5C6E] mb-2" data-testid={`quiz-upsell-tagline-${u.id}`}>
                  {u.tagline}
                </p>
              )}
              <p className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#1A5C6E] mb-3">
                ${u.total} total · Add ${u.addDollars}
              </p>
              {u.description && (
                <p className="font-body text-sm text-[#0A1F2E]/70 leading-relaxed mb-4" data-testid={`quiz-upsell-description-${u.id}`}>
                  {u.description}
                </p>
              )}
              <ul className="space-y-2.5 mb-6 flex-1" data-testid={`quiz-upsell-bullets-${u.id}`}>
                {u.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-[#0A1F2E]/85">
                    <CheckCircle2 className="w-4 h-4 text-[#2CA8A0] flex-none mt-0.5" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => handleUpgrade(u.id)}
                disabled={!!busy}
                data-testid={`quiz-upsell-cta-${u.id}`}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] disabled:opacity-70 disabled:cursor-wait text-white font-semibold py-3 rounded-full transition-colors"
              >
                {busy === u.id ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Starting upgrade…</>
                ) : (
                  <>Add {u.title} - ${u.addDollars}</>
                )}
              </button>
            </article>
          ))}
        </div>

        {error && (
          <p className="text-sm text-red-600 text-center mb-3" data-testid="quiz-upsell-error">{error}</p>
        )}

        <div className="text-center">
          <button
            type="button"
            onClick={handleSkip}
            disabled={!!busy}
            data-testid="quiz-upsell-skip"
            className="text-sm text-[#0A1F2E]/55 hover:text-[#1A5C6E] underline underline-offset-4 disabled:opacity-50"
          >
            No thanks, continue with Blueprint Only
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizUpsellPage;
